/* ==========================================================================
   Duralok shutter configurator - structural enhancement layer
   Companion to msh-opc-fixes.css. Design concept: "ARCHITECTURAL SPEC".

   The Option Price Calculator app renders a flat run of .optionHolder divs with
   labels like "1. Installation Type *". Restyling that markup alone was not
   enough (two rejected passes), so this file RESTRUCTURES it: it wraps the
   calculator in a .msh-cfg shell with a masthead, converts each option into a
   numbered step on a rail, moves the required-asterisk noise out of the way,
   pairs the Width/Height inputs into a grid, and labels the price plate.

   Every selector here was read off the rendered DOM (dom-dump4.json on
   2026-08-16), not inferred from the app's CSS. The previous version of this
   file shipped INERT because its selectors were guessed:
     it looked for .optionSetTitle / .optionSetTitle headings - neither exists.
     the real heading is  .optionHolder > .option-list-frame > label.option-label

   The app re-renders parts of the tree when the price recalculates, so every
   mutation here is idempotent and guarded by a data attribute.
   ========================================================================== */
(function () {
    'use strict';

    var READY_ATTR = 'data-msh-ready';
    var DONE_ATTR = 'data-msh-done';
    var MARK = 'mshDone';          // dataset flag on nodes we have already processed

    /* Steps that carry a real per-option upcharge. Sourced from the SOW's
       Monument parity table; the app itself leaves .priceholder empty. Kept as
       a lookup rather than hardcoded into markup so the app remains the source
       of truth once the upcharges are configured app-side. */
    var UPCHARGES = {
        'aluminum fixed mounting brackets': '+$64.00',
        'primed': '+$90.00',
        'painted': '+$200.00',
        'custom paint': '+$250.00'
    };

    /* ----------------------------------------------------------------------
       Paint finishes (2026-09-14)

       The calculator carries Primed / Painted / Custom Paint as Finish Options
       values on every product, because they live on the ONE option record the
       base calculator owns and every clone inherits. Gray switches them on per
       product by tagging it; until then they are hidden here.

       All three carry a chip. The numbers are what Monument's backend carried on
       2026-08-26 (Primed +$90, Painted +$200, Custom Paint +$250, per pair;
       REPORT-2026-08-26.md). Monument's live PVC pages have since dropped the
       Primed choice, so today's pages show only the latter two - the $90 is from
       the 08-26 pull, set on Sean's answer to Q-963116e4, not read off the live
       site.
       ---------------------------------------------------------------------- */
    var PAINT_CHOICES = { 'primed': 1, 'painted': 1, 'custom paint': 1 };
    var PAINT_TAG = 'paint-options';

    /* Gray's per-product switch. layout/theme.liquid publishes the product's
       own tags; absent (a page that is not a product, or an older theme that
       has not been redeployed) means OFF, which is the safe direction: the
       choices stay hidden rather than appearing unannounced on a live product. */
    function paintEnabled() {
        var tags = window.mshProductTags;
        if (!tags || !tags.length) return false;
        for (var i = 0; i < tags.length; i++) {
            if (String(tags[i]).trim().toLowerCase() === PAINT_TAG) return true;
        }
        return false;
    }

    /* ----------------------------------------------------------------------
       PRE-PAINT GATE - the fix for the flash-and-charge defect (DL-0004).

       boot() polls every 400ms waiting for the app to inject the calculator, so
       for up to 400ms after the app renders, the three paint tiles were on
       screen, priced, and clickable on a product where paint is switched OFF.
       A click in that window selected Painted, survived the hide that followed
       (the app keeps its selection in a closure - the same limitation as
       DL-0003), and added $200.00 to the cart with nothing in the UI to show
       for it. Reproduced 3/3 on the live untagged flat panel product.

       No amount of tuning the JS timing closes that hole, because the hole is
       "the app painted before our JS got a turn". The only thing that beats it
       is CSS already in the document when the app renders. We can do that:
       theme.liquid publishes window.mshProductTags in <head> BEFORE this
       deferred script, so by the time this line runs we already know whether
       paint is on - without waiting for a single DOM node.

       Keyed on the option-value ids of the SHARED Finish Options record, which
       is what every current calculator points at. A future calculator re-copied
       with "Copy with New Options" gets fresh ids that this gate will not match;
       that case falls back to the observer below, which is a frame rather than
       400ms. Both are belt and braces over hidePaintChoices().

       Deliberately NOT a blanket "hide every tile until enhanced": if this
       script ever throws before enhance() runs, a blanket gate would leave the
       calculator unusable, whereas this one just leaves paint hidden, which is
       the safe direction. */
    var PAINT_VALUE_IDS = [
        '6aa962bdcb9936c8502ebb59',   // Primed
        '6aa962bdcb9936c8502ebb5a',   // Painted
        '6aa962bdcb9936c8502ebb5b'    // Custom Paint
    ];

    /* The two dependent sub-steps, by option id. The .optionHolder carries the
       option id as its own id, so these need no :has().

       These belong in the gate for exactly the same reason the tiles do. The
       first version of this fix covered only the tiles, and the done-claims
       adversary found the other half still flashing on the live untagged page:
       the nine-swatch "Paint Color" panel and the "Custom Paint Color" box were
       visible for up to ~267ms, 8/8 runs, while Finish Options correctly showed
       Unfinished alone. Not chargeable - the colours are $0 and a click at first
       hit-test did not stick, 4/4 - but it is paint, on screen, on a product
       where paint is switched off. hidePaintSubSteps() hides them from inside
       enhance(), which is the JS-timing path this gate exists to replace. */
    var PAINT_STEP_IDS = [
        '6aa969e7469b0c693dce7654',   // Paint Color        (9 swatches)
        '6aa96c67cb9936c8502f1045'    // Custom Paint Color (text)
    ];

    /* Name-keyed half of the gate (see preGatePaint). Exact app value names:
       the nine colours appear in no other step on any calculator (checked on
       all six calculator products, 2026-09-27). */
    var PAINT_NAMES = ['Primed', 'Painted', 'Custom Paint'];
    var PAINT_COLOR_NAMES = ['Black', 'Black Forest Green', 'Charcoal Slate',
        'Hamilton Blue', 'Chrome Green', 'Cottage Red', 'Tudor Brown',
        'Country Redwood', 'White'];

    (function preGatePaint() {
        if (paintEnabled()) return;          // tagged: the tiles are meant to show
        if (document.getElementById('msh-paint-pre-gate')) return;
        var sel = PAINT_VALUE_IDS.map(function (id) {
            /* The id sits on .swatch-input-holder; the price/label text is a
               SIBLING, so hide the whole tile, not just the clickable box.

               [id="..."] rather than #...: these ids start with a DIGIT, and a
               CSS id selector may not. `#6aa962...` is invalid, and one invalid
               selector in a list drops the WHOLE rule - the gate was in the
               document, matching nothing, and the tiles still flashed. The
               attribute form has no such restriction. */
            return '.swatch-title-holder:has(> [id="' + id + '"])';
        }).concat(PAINT_STEP_IDS.map(function (id) {
            return '.optionHolder[id="' + id + '"]';
        })).join(',');
        /* 2026-09-27: the ids above belong to the flat panel's records only.
           Louvered, Board & Batten and Raised Panel each got their own paint
           values (own ids), so the tiles and the colour picker are ALSO gated
           by NAME, which every calculator shares and a future one will too:
           the app writes the value name onto the radio as data-value. The
           custom-paint box is a text input with no name on it, so it stays
           id-keyed above.

           A separate rule, not one list: an invalid or unsupported selector
           drops only its own rule, so the id gate survives whatever happens
           to this one. */
        var byName = PAINT_NAMES.map(function (n) {
            return '.swatch-title-holder:has(> .swatch-input-holder > input.swatch-input[data-value="' + n + '"])';
        }).concat(PAINT_COLOR_NAMES.map(function (n) {
            return '.optionHolder:has(input.swatch-input[data-value="' + n + '"])';
        })).join(',');
        var style = document.createElement('style');
        style.id = 'msh-paint-pre-gate';
        style.textContent = sel + '{display:none !important}\n' +
                            byName + '{display:none !important}';
        (document.head || document.documentElement).appendChild(style);
    })();

    /* Read an element's OWN text, ignoring anything we appended into it.
       enhanceUpcharges appends the "+$200.00" chip INTO .swatch-title-text, so
       txt() on a painted tile returns "Painted +$200.00" and every lookup keyed
       on the value name misses. enhanceTileImages already reads text nodes only
       for exactly this reason; this is that read, named and shared. */
    function rawText(el) {
        if (!el) return '';
        var out = '';
        for (var i = 0; i < el.childNodes.length; i++) {
            if (el.childNodes[i].nodeType === 3) out += el.childNodes[i].nodeValue;
        }
        return out.replace(/\s+/g, ' ').trim();
    }

    /* Option-value imagery (Gray, 2026-08-19: "I do think the images are
       helpful", referencing Monument's hover previews on Installation Type).

       Keyed by the value label lowercased, exactly as .swatch-title-text renders
       it. Sourced from Monument's own renders - both stores are Gray's - and
       resized to 460px (prep_tile_images.py); the originals were ~450KB each.

       TOP PROFILE IS DELIBERATELY ABSENT. Monument's Arch-Top asset is a grey
       LOUVERED shutter that they reuse across every product line, including the
       composite pages; putting it on a composite flat panel tile would show the
       customer a different product. Confirmed 2026-08-19 against Monument's
       raised-panel and rustic-panel composite pages - same single asset. Straight
       has a correct composite render, but shipping an image on one tile of a pair
       and not the other reads as a loading failure, so the step stays text-only
       until Gray supplies an arched composite render.

       INSTALLATION TYPE ONLY. Configuration was covered too in the first pass and
       Sean cut it (2026-08-19): the seven configuration names describe themselves,
       so a preview there is noise, whereas "Aluminum Fixed Mounting Brackets" vs
       "Functioning or Other Hardware" is a genuine what-does-that-look-like
       question. The configuration renders stay harvested and uploaded, so
       restoring them is one line if that changes. */
    var TILE_IMAGES = {
        'aluminum fixed mounting brackets': 'msh-opt-aluminum-fixed-mounting-brackets.jpg',
        'functioning or other hardware': 'msh-opt-functioning-or-other-hardware.jpg'
    };

    /* Monument's springline help icon carries a diagram (id="hover-image",
       image-1.png) explaining what a springline measurement is. Same asset. */
    var SPRINGLINE_DIAGRAM = 'msh-springline-diagram.png';

    /* Theme assets are served from a versioned CDN path we cannot build in JS,
       so derive it from this file's own URL rather than hardcoding a domain.
       Captured at parse time because document.currentScript is null once the
       script finishes executing. */
    var ASSET_BASE = (function () {
        var s = document.currentScript
             || document.querySelector('script[src*="msh-opc-enhance"]');
        if (!s || !s.src) return null;
        return s.src.replace(/msh-opc-enhance\.js.*$/, '');
    }());

    function txt(el) {
        return (el && el.textContent ? el.textContent : '').replace(/\s+/g, ' ').trim();
    }

    /* "1. Installation Type *" -> {num: "1", name: "Installation Type"} */
    function parseLabel(raw) {
        var s = raw.replace(/\s+/g, ' ').trim();
        var required = /\*\s*$/.test(s);
        s = s.replace(/\*\s*$/, '').trim();
        var m = s.match(/^(\d+)\s*[.)]\s*(.+)$/);
        return {
            num: m ? m[1] : null,
            name: m ? m[2].trim() : s,
            required: required
        };
    }

    function buildShell(stepHolder) {
        if (stepHolder.closest('.msh-cfg')) return stepHolder.closest('.msh-cfg');

        // NEVER reparent stepHolder. Moving it into a shell of our own made the
        // app abandon add to cart silently: no request, no error, empty cart
        // (isolated 2026-08-17 by serving this file with only that line removed,
        // which restored the add at the correct $589.00). The app resolves its
        // options from this node's original position at submit time.
        // stepHolder's parent is an unclassed div wrapping nothing else, so we
        // promote THAT to the shell and move no app node at all.
        var shell = stepHolder.parentElement;
        if (!shell || shell.children.length !== 1) {
            // defensive: if the app's markup ever changes shape, decorate in
            // place rather than restructuring and breaking the cart again
            shell = stepHolder;
        }
        shell.classList.add('msh-cfg');

        // The app's own "Build Your Shutters" bar, if present, becomes the title
        // rather than a second competing header.
        var appTitle = document.querySelector('.optionTitle');
        var titleText = appTitle ? txt(appTitle) : '';
        if (appTitle) appTitle.style.display = 'none';
        if (!titleText) titleText = 'Build Your Shutters';

        var head = document.createElement('div');
        head.className = 'msh-cfg__head';
        head.innerHTML =
            '<div>' +
            '<span class="msh-cfg__eyebrow">Configure your pair</span>' +
            '<h2 class="msh-cfg__title"></h2>' +
            '</div>' +
            '<p class="msh-cfg__note">Choose your options below. Your price updates as you build.</p>';
        head.querySelector('.msh-cfg__title').textContent = titleText;

        // head goes IN as a new first child; stepHolder itself never moves
        shell.insertBefore(head, shell.firstChild);

        // Pull the price plate inside the shell so it reads as the configurator's
        // foot rather than a detached strip. Moving this element is safe: the
        // isolation run kept this line and add to cart still worked.
        var price = document.querySelector('.option-price-container');
        if (price) shell.appendChild(price);

        return shell;
    }

    function enhanceHeading(holder, fallbackIndex) {
        var frame = holder.querySelector(':scope > .option-list-frame.option-name-display')
                 || holder.querySelector(':scope > div');
        if (!frame) return;

        var label = frame.querySelector('label.option-label');
        if (!label || label.dataset[MARK]) return;

        var parsed = parseLabel(txt(label));

        // Rewrite the label: drop the leading "N." (the badge carries it) and
        // isolate the trailing asterisk so CSS can hide it.
        label.textContent = '';
        var nameSpan = document.createElement('span');
        nameSpan.className = 'msh-step-name';
        var isQty = /^quantity$/i.test(parsed.name);
        /* Springline is not one of the numbered steps. On Monument it sits under
           Top Profile as a plain "Springline" field with no number and no
           required asterisk, appearing only when Arched Top is chosen. Verified
           on their rendered page 2026-08-19: hidden at height 0 with Straight,
           81px with Arched, label text "Springline". */
        var isSpring = /springline/i.test(parsed.name);
        /* Paint Color and Custom Paint Color are conditional sub-fields of the
           finish choice, exactly as Springline is of Top Profile: they appear
           only once a paint finish is picked. They are not numbered steps.
           Their labels carry no leading "N.", so before this the badge fell
           through to the DOM index and the rail rendered 1 2 3 4 [8] 5 6 with
           Paint Color wedged between steps 4 and 5 (adversary, 2026-09-16).
           "Finish Options" does not match /paint/, so the finish step itself
           keeps its number. */
        var isPaintSub = /paint/i.test(parsed.name);
        nameSpan.textContent = (isQty || isSpring)
            ? parsed.name
            : 'Select ' + parsed.name;
        label.appendChild(nameSpan);

        // Gray, 2026-08-19: "(Pairs of Shutters)" beside Quantity - "I know its
        // redundant but its helpful reiteration". Shutters sell in pairs and the
        // price plate reads "Price per pair", so the number in this box is pairs,
        // not panels. Cheap to say, expensive to get wrong at checkout.
        if (isQty) {
            var sub = document.createElement('span');
            sub.className = 'msh-step-sub';
            sub.textContent = '(Pairs of Shutters)';
            label.appendChild(sub);
        }
        if (parsed.required) {
            var req = document.createElement('span');
            req.className = 'msh-req';
            req.textContent = ' *';
            label.appendChild(req);
        }
        label.dataset[MARK] = '1';

        // Number badge on the rail. Springline is a conditional sub-field of Top
        // Profile, not a step of its own, so it never gets one.
        if (isSpring) {
            holder.setAttribute('data-msh-substep', 'springline');
        } else if (isPaintSub) {
            holder.setAttribute('data-msh-substep', 'paint');
        } else if (!holder.querySelector(':scope > .msh-step-num')) {
            var badge = document.createElement('span');
            badge.className = 'msh-step-num';
            badge.setAttribute('aria-hidden', 'true');
            badge.textContent = parsed.num || String(fallbackIndex);
            holder.insertBefore(badge, holder.firstChild);
        }
    }

    /* Springline's help icon, carrying Monument's own explainer diagram. Their
       markup is <span class="springline_icon"><i class="fa fa-question-circle">
       <img id="hover-image" ...></i></span>, i.e. a question mark that reveals
       the diagram on hover; this is the same idea with our own markup.

       The panel is position:absolute inside the holder for the same reason the
       tile previews are: an ancestor transform would capture a fixed-position
       child, which is how the mobile tooltip broke on 2026-08-17. */
    function enhanceSpringlineHelp(holder) {
        if (!ASSET_BASE) return;
        if (holder.getAttribute('data-msh-substep') !== 'springline') return;
        if (holder.querySelector('.msh-spring-help')) return;
        var label = holder.querySelector('label.option-label');
        if (!label) return;

        var wrap = document.createElement('span');
        wrap.className = 'msh-spring-help';

        var dot = document.createElement('button');
        dot.type = 'button';                 // never submit the product form
        dot.className = 'msh-spring-dot';
        dot.setAttribute('aria-label', 'What is a springline measurement?');
        dot.textContent = '?';

        var panel = document.createElement('span');
        panel.className = 'msh-spring-panel';
        var img = document.createElement('img');
        img.src = ASSET_BASE + SPRINGLINE_DIAGRAM;
        img.alt = 'Springline is the height at which the side of the window begins to arch.';
        img.loading = 'lazy';
        panel.appendChild(img);

        // Tap-to-toggle for touch, where there is no hover at all.
        dot.addEventListener('click', function (e) {
            e.preventDefault();
            e.stopPropagation();
            wrap.classList.toggle('is-open');
        });

        wrap.appendChild(dot);
        wrap.appendChild(panel);
        label.appendChild(wrap);
    }

    /* Monument shows Springline directly beneath Top Profile. The app hands it to
       us LAST, after Quantity, so it needs repositioning.

       Done with CSS order, never by moving the node. Reparenting app markup is
       what silently killed add to cart on 2026-08-17 - no request, no error,
       empty cart - and the app resolves its options from their original DOM
       positions at submit time. Setting .style.order changes only paint order, so
       the DOM the app reads is untouched. The container is display:block by
       default, so the stylesheet makes .stepHolder a flex column for this to
       take effect. */
    function orderSteps(shell) {
        /* 'paint' sits directly after 'finish' so the colour picker and the
           custom-paint box render as sub-steps of the finish choice that
           reveals them, rather than sinking to ord 99 below Quantity. */
        var ORDER = ['installation', 'top profile', 'springline', 'configuration',
                     'finish', 'paint', 'dimension', 'quantity'];
        var lastEl = null, lastOrder = -1;
        shell.querySelectorAll('.optionHolder').forEach(function (h) {
            var lab = h.querySelector('label.option-label');
            var name = lab ? txt(lab).toLowerCase() : '';
            var ord = 99;               // anything unrecognised sinks to the end
            for (var i = 0; i < ORDER.length; i++) {
                if (name.indexOf(ORDER[i]) !== -1) { ord = i + 1; break; }
            }
            h.style.order = String(ord);
            h.removeAttribute('data-msh-visual-last');
            if (ord > lastOrder) { lastOrder = ord; lastEl = h; }
        });
        // The connecting rail is drawn per holder and must stop at the bottom of
        // the visible run. That is whichever holder sorts last on SCREEN, which is
        // no longer the last one in the DOM once Springline moves up.
        if (lastEl) lastEl.setAttribute('data-msh-visual-last', '1');
    }

    /* Inline upcharge chips, Monument-style: "(+$64.00)" beside the choice. */
    function enhanceUpcharges(holder) {
        holder.querySelectorAll('.swatch-description-holder').forEach(function (d) {
            if (d.dataset[MARK]) return;
            var t = d.querySelector('.swatch-title-text');
            if (!t) return;
            var key = txt(t).toLowerCase();
            if (UPCHARGES[key]) {
                var chip = document.createElement('span');
                chip.className = 'msh-upcharge';
                chip.textContent = UPCHARGES[key];
                t.appendChild(document.createElement('br'));
                t.appendChild(chip);
            }
            d.dataset[MARK] = '1';
        });
    }

    /* Put the product render on the tile, with a larger preview on hover -
       Monument's pattern (their image swatches carry data-zoom-url).

       The preview panel goes in .swatch-title-holder, NOT in the description
       holder, and is position:absolute rather than fixed. The description holder
       carries a hover transform, and a transformed ancestor becomes the containing
       block for position:fixed descendants - the exact bug that collapsed the
       mobile help tooltip to a 23px column thousands of pixels tall on
       2026-08-17. .swatch-title-holder has no transform, so an absolutely
       positioned child of it is safe.

       Hover-only affordances are gated in CSS on (hover: hover), so touch devices
       get the inline image and no preview. Tapping a tile on a phone fires :hover
       in most mobile browsers, which is how that same tooltip bug reached mobile. */
    function enhanceTileImages(holder) {
        if (!ASSET_BASE) return;
        holder.querySelectorAll('.swatch-title-holder').forEach(function (tile) {
            if (tile.dataset.mshImg) return;
            var textEl = tile.querySelector('.swatch-title-text');
            if (!textEl) return;

            // The upcharge chip is appended into .swatch-title-text, so read the
            // FIRST text node rather than textContent - otherwise the key for the
            // brackets tile becomes "aluminum fixed mounting brackets +$64.00".
            var raw = '';
            for (var i = 0; i < textEl.childNodes.length; i++) {
                var n = textEl.childNodes[i];
                if (n.nodeType === 3) raw += n.nodeValue;
            }
            var key = raw.replace(/\s+/g, ' ').trim().toLowerCase();
            var file = TILE_IMAGES[key];
            if (!file) { tile.dataset.mshImg = 'none'; return; }

            /* No inline thumbnail on the tile. Monument's own swatches are
               TEXT ONLY - their <img> renders display:none / opacity:0 / 0x0 and
               the artwork is reached through the swatch's data-zoom-url on hover.
               Observed on their rendered composite flat panel page 2026-08-19,
               after an earlier read of their markup alone wrongly suggested the
               images sat on the tiles. Gray also asked to keep the form clean
               ("I like how clean the form is at current state"), so the image is
               a hover affordance and the tile face stays untouched. */
            var prev = document.createElement('span');
            prev.className = 'msh-tile-preview';
            prev.setAttribute('aria-hidden', 'true');
            var pimg = document.createElement('img');
            pimg.src = ASSET_BASE + file;
            pimg.alt = '';
            pimg.loading = 'lazy';
            prev.appendChild(pimg);
            tile.appendChild(prev);

            tile.dataset.mshImg = '1';
        });
    }

    /* The calculator still carries a dead "Custom Configuration" choice that
       does nothing. Removing it properly is an app-side change (SOW item); this
       hides it in the meantime so customers cannot pick a no-op. */
    function hideDeadChoices(holder) {
        holder.querySelectorAll('.swatch-title-holder').forEach(function (h) {
            var t = h.querySelector('.swatch-title-text');
            if (t && /^custom configuration$/i.test(txt(t))) {
                h.style.display = 'none';
                h.setAttribute('data-msh-hidden', 'dead-option');
            }
        });
    }

    /* Does this calculator actually carry the sub-step a finish depends on?

       Not every calculator has the paint sub-steps. Rustic Panel shares the
       Finish Options record with Flat Panel (same option, same prices, Sean told
       Gray so on 2026-08-28), but it does NOT have Paint Color or Custom Paint
       Color of its own - 7 options / 4 rules against flat panel's 9 / 6. So on a
       tagged rustic product the Painted tile would appear, charge +$200.00, and
       leave the shopper no way to say WHICH colour. Same for Custom Paint's
       +$250.00 code box.

       Keyed on the rendered step label rather than an option id, so it keeps
       working across clones (which get fresh ids) and renames. Presence is what
       matters, not visibility - the sub-steps ship hidden and are revealed by
       selection, so display state says nothing about whether they exist.

       Primed needs no sub-step: it is a finish on its own. */
    function paintSubStepPresent(choice) {
        if (choice === 'primed') return true;
        var wantCustom = /custom\s*paint/.test(choice);
        var holders = document.querySelectorAll('.msh-cfg .optionHolder');
        for (var i = 0; i < holders.length; i++) {
            var l = holders[i].querySelector('label.option-label');
            if (!l) continue;
            var name = txt(l).toLowerCase();
            if (name.indexOf('paint') === -1) continue;
            var isCustom = /custom\s*paint/.test(name);
            if (wantCustom ? isCustom : (!isCustom && /colo/.test(name))) return true;
        }
        return false;
    }

    /* The paint finishes live on the shared option record, so they exist on
       EVERY product the moment they are added to the base calculator. This is
       the switch that keeps them invisible until Gray tags the product.

       Called from enhance(), never from boot(): the app rebuilds its option
       markup on every recalculation and the MutationObserver re-runs enhance()
       against the new nodes, so a one-shot hide would come undone the first
       time a shopper changed anything.

       The unchecking guard is the safety net. A value saved app-side with its
       "default" box ticked would arrive pre-selected; hidden AND selected means
       the shopper is silently charged for a choice they cannot see. Nothing
       ships that way today (all values read default=false on 2026-09-14), so
       this is belt and braces against a later edit in the app admin. */
    function hidePaintChoices(holder) {
        var label = holder.querySelector('label.option-label');
        if (!label || !/finish/i.test(txt(label))) return;
        var on = paintEnabled();
        holder.querySelectorAll('.swatch-title-holder').forEach(function (h) {
            var t = h.querySelector('.swatch-title-text');
            if (!t) return;
            var choice = rawText(t).toLowerCase();
            if (!PAINT_CHOICES[choice]) return;
            var why = !on ? 'paint-off'
                : !paintSubStepPresent(choice) ? 'paint-no-substep'
                : '';
            if (!why) {
                var was = h.getAttribute('data-msh-hidden');
                if (was === 'paint-off' || was === 'paint-no-substep') {
                    h.removeAttribute('data-msh-hidden');
                    h.style.removeProperty('display');
                }
                return;
            }
            h.style.display = 'none';
            h.setAttribute('data-msh-hidden', why);
            var input = h.querySelector('input.swatch-input');
            if (input && input.checked) {
                input.checked = false;
                input.dispatchEvent(new Event('change', { bubbles: true }));
            }
        });
    }

    /* Paint Color / Custom Paint Color are app-side optional options revealed
       by the app's own rules when a paint finish is picked. With paint switched
       off they can never be revealed, but a rule that is mis-saved later would
       expose them, so the step itself is hidden too - same treatment the
       springline sub-step already gets. */
    function hidePaintSubSteps() {
        /* Which sub-step belongs to which finish. Keyed on the step label so a
           renamed option keeps working as long as it still says "colour" or
           "custom paint". */
        var finish = paintEnabled() ? selectedPaintFinish() : '';
        document.querySelectorAll('.msh-cfg .optionHolder').forEach(function (h) {
            var l = h.querySelector('label.option-label');
            if (!l) return;
            var name = txt(l).toLowerCase();
            if (name.indexOf('paint') === -1) return;

            var wanted;
            if (!paintEnabled()) {
                wanted = false;                       // paint off: nothing shows
            } else if (/custom\s*paint/.test(name)) {
                wanted = finish === 'custom paint';   // the code box
            } else {
                wanted = finish === 'painted';        // the colour picker
            }

            if (wanted) {
                h.removeAttribute('data-msh-hidden');
                h.style.removeProperty('display');
            } else {
                h.setAttribute('data-msh-hidden', 'paint-step');
                h.style.display = 'none';
                clearStep(h);
            }
        });
    }

    /* A hidden sub-step must not keep its value. The app sends every option it
       holds to the cart line, so a colour picked under Painted survived a switch
       back to Unfinished and rode along as "Paint Color = Hamilton Blue" on an
       unfinished shutter - the price was right, the order was not. Observed on
       the live calculator 2026-09-15. Clearing is safe against a loop: the
       change we dispatch re-runs this pass, finds nothing selected, and stops. */
    function clearStep(holder) {
        holder.querySelectorAll('input.swatch-input').forEach(function (input) {
            if (!input.checked) return;
            input.checked = false;
            input.dispatchEvent(new Event('change', { bubbles: true }));
        });
        holder.querySelectorAll('input.text-input, textarea').forEach(function (input) {
            if (!input.value) return;
            input.value = '';
            input.dispatchEvent(new Event('input', { bubbles: true }));
            input.dispatchEvent(new Event('change', { bubbles: true }));
        });
    }

    /* Width and Height are two sibling .option-list-frame blocks. Group them so
       they sit side by side instead of stacking full width. */
    function groupDimensions(holder) {
        var frames = holder.querySelectorAll(':scope > .option-list-frame:not(.option-name-display)');
        if (frames.length < 2 || holder.querySelector(':scope > .msh-dims')) return;
        var wrap = document.createElement('div');
        wrap.className = 'msh-dims';
        frames[0].parentNode.insertBefore(wrap, frames[0]);
        frames.forEach(function (f) { wrap.appendChild(f); });

        // State the unit once per field rather than leaving bare number boxes.
        wrap.querySelectorAll('.inputHolder').forEach(function (ih) {
            if (ih.querySelector('.msh-unit')) return;
            var hint = document.createElement('span');
            hint.className = 'msh-unit';
            hint.textContent = 'Inches';
            ih.appendChild(hint);
        });

        /* The Width dropdown OFFERS values the calculator then refuses. Picking
           10.00 prices fine on the plate but Add to Cart dies with the app's own
           "The minimum for Width is 12" - eight dead choices (10.00 to 11.75)
           that reject the shopper only after they commit. Measured 2026-08-17:
           w=10/h=16 and w=10/h=60 both blocked, w=12/h=16 and w=18/h=16 both
           added fine, so the floor is on WIDTH alone.

           Enforced minimum, per the app's message. The app's own option list is
           the wrong source for the hint precisely because it disagrees with this.
           Clearing the sub-12 values app-side is the durable fix and needs the
           calculator editor; this makes the storefront correct meanwhile. */
        /* Must track lookup.input1Min in the app, which is itself derived from the
           smallest value in row 1 of the 'Shutter L W' pricing table. That minimum
           was 12 while the dropdown offered 10.00-11.75, so those eight priced on
           screen and then failed validation at Add to Cart. The pricing table now
           starts at 10, matching Monument, so nothing needs pruning; the floor stays
           here as a guard if the two ever drift apart again. */
        var MIN_BY_FIELD = { width: 10 };

        wrap.querySelectorAll('.inputHolder').forEach(function (ih) {
            var sel = ih.querySelector('select.option-dropdown-input');
            if (!sel || sel.options.length < 3) return;

            var frame = ih.closest('.option-list-frame') || ih.parentNode;
            /* Match on the frame's LEADING text, not a label lookup: querying
               'label, .option-label' inside the frame returned an element with no
               field name in it, so the floor never applied (verified on the
               storefront - width still offered 10.00). The frame's own innerText
               begins with the field name, and the slice keeps the 57 option labels
               that follow out of the comparison. */
            var name = (frame.innerText || '').slice(0, 40).toLowerCase();
            var floor = null;
            Object.keys(MIN_BY_FIELD).forEach(function (k) {
                if (name.indexOf(k) !== -1) floor = MIN_BY_FIELD[k];
            });

            /* Drop the values the calculator will reject. Disabling rather than
               removing would leave them visible and still readable as offered,
               and the app reads the SELECTED value only, so pruning options it
               would refuse anyway does not touch anything it depends on. Guard
               the current selection: never prune the option in use. */
            if (floor !== null && !ih.hasAttribute('data-msh-pruned')) {
                for (var j = sel.options.length - 1; j >= 0; j--) {
                    var ov = parseFloat(sel.options[j].value);
                    if (!isNaN(ov) && ov < floor && !sel.options[j].selected) {
                        sel.remove(j);
                    }
                }
                ih.setAttribute('data-msh-pruned', String(floor));
            }

            if (ih.querySelector('.msh-range')) return;
            var vals = [];
            for (var i = 0; i < sel.options.length; i++) {
                var v = parseFloat(sel.options[i].value);
                if (!isNaN(v)) vals.push(v);
            }
            if (vals.length < 2) return;
            var lo = Math.min.apply(null, vals), hi = Math.max.apply(null, vals);
            if (floor !== null) lo = Math.max(lo, floor);
            var range = document.createElement('span');
            range.className = 'msh-range';
            range.textContent = lo + ' to ' + hi + ' in quarter-inch steps';
            ih.appendChild(range);
        });
    }

    /* The quantity box ships with max="undefined", a literal invalid attribute
       (SOW: "an invalid quantity limit").

       REMOVING the attribute breaks the whole calculator, and this cost most of
       2026-08-17 to find. With the attribute gone the app cannot read quantity,
       so it never computes a price: the plate stays "-", and because the app
       refuses to submit without a price, Add to Cart does NOTHING - no request,
       no error, empty cart. A silent dead button, on the only path to purchase.
       Measured, one variable at a time, in real Chrome:
           call removed entirely     -> plate $589.00, add succeeds
           set max="9999"            -> plate $589.00, add succeeds
           removeAttribute('max')    -> plate "-",     add dead
       The marker attribute was not the problem; the removal was.

       So the attribute must EXIST and must PARSE. 9999 is high enough that the
       browser will never block a realistic quantity the app would have accepted,
       which is the FrugalText failure mode this originally tried to avoid. */
    function fixQuantityLimits(holder) {
        holder.querySelectorAll('.opc-quantity').forEach(function (q) {
            var max = q.getAttribute('max');
            if (max === 'undefined' || max === 'NaN' || max === 'null') {
                q.setAttribute('max', '9999');
                q.setAttribute('data-msh-max-fixed', max);
            }
        });
    }

    /* The total renders as a bare figure (and as "-" before any input). Give it
       a caption so it is never an unlabelled number. */
    /* Guard on the caption ACTUALLY being present, not on a marker. The app
       rewrites .option-price-label's innerHTML on every recalculation, which
       wipes the caption while leaving the marker on the surviving label node -
       so a dataset guard silently stopped re-adding it after the first price
       update, and the total shipped unlabelled. */
    function enhancePrice(shell) {
        var label = shell.querySelector('.option-price-label');
        if (!label || label.querySelector('.msh-price-caption')) return;
        var cap = document.createElement('span');
        cap.className = 'msh-price-caption';
        // The formula is a UNIT price (Shopify applies quantity), so this figure
        // does NOT change with quantity. Calling it "Your total" was misleading
        // at qty>1 - the plate showed $709 while the cart charged $2,127.
        cap.textContent = 'Price per pair';
        label.insertBefore(cap, label.firstChild);

        var holder = label.querySelector('.addition-price-holder');
        if (holder && !holder.querySelector('.msh-price-sub')) {
            var sub = document.createElement('span');
            sub.className = 'msh-price-sub';
            sub.textContent = 'Before tax and shipping. Choose quantity above.';
            holder.appendChild(sub);
        }
    }

    /* ----------------------------------------------------------------------
       Configuration-driven main image

       The first version of this added all seven configuration renders to the
       product as gallery media and NAVIGATED to the matching slide. It worked,
       but it dragged every render into the gallery - eleven thumbnails on a
       product with four real photographs - and it does not scale: add a finish
       dimension and the same approach needs forty two slides.

       This version keeps ONE slide as a canvas (media 25245239771234, the 50/50
       front render that was already the lead image) and REWRITES its image
       source from the map below. The renders live in Shopify Files instead of
       product media, so they never appear in the gallery at all, and another
       option dimension later costs a map entry rather than a slide.

       Rewriting src alone is NOT enough, and this is the part that bites:
         - the browser resolves the displayed image from SRCSET, so a stale
           srcset keeps the old shutter on screen no matter what src says;
         - PhotoSwipe zoom reads data-src and data-pswp-* off the SLIDE element,
           not off the img, so zoom would open the previous configuration;
         - the thumbnail is a separate <img> and would disagree with the main
           image.
       All four are written together, or the gallery contradicts itself.

       Falls back to the old alt-text slide match when a configuration has no map
       entry, so re-adding media by hand still works.
       ---------------------------------------------------------------------- */

    var CANVAS_MEDIA_ID = '25245239771234';
    var CDN = 'https://cdn.shopify.com/s/files/1/0575/7262/2434/files/';
    /* Width ladders are deliberately SHORT. Shopify generates each width variant
       on demand at origin: measured 2026-08-17, a first-ever request for a width
       costs 487-857ms while the same variant warm costs 43-53ms. The theme's own
       ladder offers eleven widths, which across seven renders is ~77 variants
       that mostly nobody has ever requested - and a retina shopper asks for a
       different one than a 1x shopper, so most switches paid the cold price.
       Four widths concentrate every shopper onto variants that stay warm.
       The canvas is at most 603 CSS px (see the img sizes attribute), so 1800
       already covers 3x. */
    var NATURAL = 1800;
    var MAIN_WIDTHS = [600, 900, 1200, 1800];
    var THUMB_WIDTHS = [104, 208];

    /* Keyed by normalise() of the Configuration swatch label: "50/50" -> "5050".
       Files are standalone Shopify Files (uploaded 2026-08-17), deliberately NOT
       product media - that is the whole point of this approach. */
    var CONFIG_IMAGES = {
        'single':   'Composite-Clear-Single_Front_0cf09350-9f8e-48f6-b1d1-2690cf2f0c9f.png?v=1786979206',
        '5050':     'Composite-Clear-50_50-Front_37b71b5c-cff6-43cc-a279-8b0f823a0055.png?v=1786979206',
        '4060':     'Composite-Clear-40_60-Front_460c8970-22df-4eef-9a68-8b6d6c04a5da.png?v=1786979206',
        '6040':     'Composite-Clear-60_40-Front_d11833f9-7723-4175-94ee-9e79fe1391c0.png?v=1786979206',
        '333333':   'Composite-Clear-33_33_33-Front_cf8f24f2-0295-4438-bd6b-73aaf633515f.png?v=1786979206',
        '204040':   'Composite-Clear-20_40_40-Front_06b82ccc-4fbb-425b-b74b-5fde274f6275.png?v=1786979207',
        '402040':   'Composite-Clear-40_20_40-Front_6a764a76-e2c3-435d-90f2-a63917c0a2d8.png?v=1786979206'
    };

    function normalise(s) {
        return (s || '').toLowerCase().replace(/[^a-z0-9]/g, '');
    }

    /* The flat panel's own seven configuration names. Kept only as the fallback
       for the moment before the calculator has rendered its steps, when there is
       no Configuration step in the DOM to read names off. */
    var LEGACY_CONFIG_KEYS = { single: 1, '5050': 1, '4060': 1, '6040': 1,
                               '333333': 1, '204040': 1, '402040': 1 };

    /* 2026-08-27: this used to BE the hardcoded list above, which meant the
       thumbnail strip only collapsed on products whose configuration names
       happened to match the flat panel's. Gray's board and batten (3x2, 4x2,
       5x2, and the Z Bar variants) matched none of them, so it showed all six
       renders at once. Read the names off the product's own Configuration step
       instead, so every product collapses, including ones not built yet. */
    function configKeys() {
        var keys = {}, found = false;
        var holders = document.querySelectorAll('.msh-cfg .optionHolder');
        for (var i = 0; i < holders.length; i++) {
            var label = holders[i].querySelector('label.option-label');
            if (!label || !/configuration/i.test(txt(label))) continue;
            var titles = holders[i].querySelectorAll('.swatch-title-text');
            for (var j = 0; j < titles.length; j++) {
                var key = normalise(txt(titles[j]));
                if (!key) continue;
                keys[key] = 1;
                found = true;
            }
            break;
        }
        return found ? keys : LEGACY_CONFIG_KEYS;
    }

    /* Every alt text that names an OPTION rather than a photograph.

       hideOtherConfigThumbs treats any thumbnail whose whole alt is one of
       these as a swappable render and shows only the active one; anything else
       is a real photograph and is left alone. Before paint that set was the
       configuration names alone, which meant a product carrying a "Painted"
       render would show it as a permanent extra thumbnail - on a product where
       paint is not even switched on yet.

       The combined keys ("50/50 Painted") are what lets one product carry a
       painted render per configuration. normalise() strips the space and the
       slash on both sides of the comparison, so the key built here and the key
       read off the alt attribute are produced identically. */
    function renderKeys() {
        var keys = configKeys(), out = {}, k;
        for (k in keys) { if (keys.hasOwnProperty(k)) out[k] = 1; }
        var finishes = Object.keys(PAINT_CHOICES);
        for (var f = 0; f < finishes.length; f++) {
            out[normalise(finishes[f])] = 1;
            for (k in keys) {
                if (keys.hasOwnProperty(k)) out[normalise(k + finishes[f])] = 1;
            }
        }
        /* Colour renders ("50/50 Painted Black", "Painted Black") are swappable
           renders too, or each one would sit in the strip as a permanent extra
           photo. Only Painted carries a colour. */
        var colors = paintColorNames();
        for (var c = 0; c < colors.length; c++) {
            out[normalise('painted' + colors[c])] = 1;
            for (k in keys) {
                if (keys.hasOwnProperty(k)) out[normalise(k + 'painted' + colors[c])] = 1;
            }
        }
        return out;
    }

    /* The Paint Color step's own value names, read off the rendered step so a
       colour Gray adds in the app is picked up without a code change. Before
       the calculator renders there is no step to read, so fall back to the
       nine names the pre-gate already knows. */
    function paintColorStep() {
        var holders = document.querySelectorAll('.msh-cfg .optionHolder');
        for (var i = 0; i < holders.length; i++) {
            var label = holders[i].querySelector('label.option-label');
            var name = label ? txt(label).toLowerCase() : '';
            if (name.indexOf('paint color') !== -1 && !/custom\s*paint/.test(name)) return holders[i];
        }
        return null;
    }

    function paintColorNames() {
        var step = paintColorStep(), out = [];
        if (step) {
            var inputs = step.querySelectorAll('input.swatch-input');
            for (var i = 0; i < inputs.length; i++) {
                if (inputs[i].getAttribute('data-value')) out.push(inputs[i].getAttribute('data-value'));
            }
        }
        return out.length ? out : PAINT_COLOR_NAMES;
    }

    /* The colour the shopper picked under Painted, or ''. A hidden colour step
       (paint off, or a finish other than Painted) counts as no colour: the
       app keeps a stale selection there after a switch back (DL-0003). */
    function selectedPaintColor() {
        var step = paintColorStep();
        if (!step || step.getAttribute('data-msh-hidden')) return '';
        var checked = step.querySelector('input.swatch-input:checked');
        return checked ? (checked.getAttribute('data-value') || '') : '';
    }

    /* The finish the shopper has actually picked, or '' for none/unfinished.
       Read with rawText so the upcharge chip we append never contaminates it. */
    function selectedPaintFinish() {
        var holders = document.querySelectorAll('.msh-cfg .optionHolder');
        for (var i = 0; i < holders.length; i++) {
            var label = holders[i].querySelector('label.option-label');
            if (!label || !/finish/i.test(txt(label))) continue;
            var checked = holders[i].querySelector('input.swatch-input:checked');
            if (!checked) return '';
            var tile = checked.closest('.swatch-title-holder');
            if (!tile || tile.getAttribute('data-msh-hidden')) return '';
            var name = rawText(tile.querySelector('.swatch-title-text')).toLowerCase();
            return PAINT_CHOICES[name] ? name : '';
        }
        return '';
    }

    function mediaIdForValue(value, exactOnly) {
        var want = normalise(value);
        if (!want) return null;
        var slides = document.querySelectorAll('.f-product__media[data-media-id]');
        var exact = null, partial = null;
        for (var i = 0; i < slides.length; i++) {
            var img = slides[i].querySelector('img');
            if (!img) continue;
            var alt = normalise(img.getAttribute('alt'));
            if (!alt) continue;
            var id = slides[i].getAttribute('data-media-id');
            if (alt === want) { exact = id; break; }
            // "Composite flat panel shutter pair, 50/50 split" contains "5050"
            if (!partial && alt.indexOf(want) !== -1) partial = id;
        }
        return exact || (exactOnly ? null : partial);
    }

    /* The theme's <media-gallery> exposes setActiveMedia(mediaId), which drives
       the Flickity instance directly. An earlier version clicked the thumbnail
       instead and was a silent NO-OP: this carousel does not respond to a
       scripted .click(), only to a real pointer event, so the swap never fired
       and my own test gave a false pass. Use the API; keep the click as a
       fallback for a theme that lacks it. */
    function showMedia(mediaId) {
        if (!mediaId) return false;
        var gallery = document.querySelector('media-gallery');
        if (gallery && typeof gallery.setActiveMedia === 'function') {
            gallery.setActiveMedia(mediaId);
            return true;
        }
        var thumb = document.querySelector(
            '.f-product__media-thumbnails-item[data-media-id="' + mediaId + '"]');
        if (!thumb) return false;
        (thumb.querySelector('button, a, img') || thumb).click();
        return true;
    }

    /* Shopify's image CDN takes &width=N, so one Files url yields the whole
       responsive set. Every candidate carries an explicit width: the bare url is
       deliberately NOT offered, because it is a variant outside the warm ladder. */
    function srcsetFor(url, widths) {
        return widths.map(function (w) {
            return url + '&width=' + w + ' ' + w + 'w';
        }).join(', ');
    }

    /* Preload the whole render set once the page is idle, using the SAME srcset
       and sizes as the live img so the browser picks the identical candidate and
       the switch becomes a memory-cache hit instead of a network round trip.
       Cheap: the CDN negotiates webp, so the display-size variant is ~25-35KB.
       Sequential, so it never competes with the page's own loading, and decoded
       up front so the switch pays no decode cost either. */
    var preloadStarted = false;

    /* True when the product carries its own configuration-named media, i.e. the
       media-first route (2026-08-24) will serve every switch and CONFIG_IMAGES is
       never consulted. Those Files are the composite flat panel's renders; once
       this layer loads on every product page (2026-09-09) preloading them on a
       pine - or any future material - page would idle-download seven images of the
       wrong product that page will never display. */
    function hasOwnConfigMedia() {
        var keys = renderKeys();
        var slides = document.querySelectorAll('.f-product__media[data-media-id] img');
        for (var i = 0; i < slides.length; i++) {
            var alt = normalise(slides[i].getAttribute('alt'));
            if (alt && keys[alt]) return true;
        }
        return false;
    }

    function preloadRenders() {
        if (preloadStarted) return;
        if (hasOwnConfigMedia()) { preloadStarted = true; return; }
        var conn = navigator.connection;
        if (conn && (conn.saveData || /(^|-)2g$/.test(conn.effectiveType || ''))) return;
        preloadStarted = true;

        var live = document.querySelector(
            '.f-product__media[data-media-id="' + CANVAS_MEDIA_ID + '"] img');
        var sizes = live ? live.getAttribute('sizes') : null;
        var keys = Object.keys(CONFIG_IMAGES);
        var i = 0;

        (function next() {
            if (i >= keys.length) return;
            var url = CDN + CONFIG_IMAGES[keys[i++]];
            var pre = new Image();
            pre.decoding = 'async';
            if (sizes) pre.sizes = sizes;
            pre.srcset = srcsetFor(url, MAIN_WIDTHS);
            pre.src = url + '&width=' + MAIN_WIDTHS[0];
            // The thumbnail is a second, separate <img>, so it was the only
            // network request left on a switch once the main image was cached.
            // 3KB each, so it rides along in the same idle pass.
            var thumb = new Image();
            thumb.decoding = 'async';
            thumb.src = url + '&width=' + THUMB_WIDTHS[THUMB_WIDTHS.length - 1];

            var step = function () { setTimeout(next, 100); };
            // error must advance the queue too, or one bad file stalls the rest
            pre.addEventListener('error', step);
            pre.addEventListener('load', function () {
                if (pre.decode) { pre.decode().then(step, step); } else { step(); }
            });
        })();
    }

    /* PhotoSwipe does NOT consult data-src when the lightbox opens: media-gallery
       builds lightbox.options.dataSource ONCE at init from those attributes and
       then works off that array. Rewriting the slide attribute alone left zoom
       opening the PREVIOUS configuration - selecting 33/33/33 and zooming showed
       the legacy 50/50 photo. Found by the done-work-adversary on 2026-08-17,
       after my own check "passed" by reading back the attribute it had just
       written. The array entry is the thing that matters. */
    function repointZoom(prevSrc, url) {
        var gallery = document.querySelector('media-gallery');
        var lb = gallery ? gallery.lightbox : null;
        var ds = lb && lb.options ? lb.options.dataSource : null;
        if (!ds || !ds.length) return false;

        var entry = null, i;
        // Identity link: the entry still holding the source we are replacing.
        for (i = 0; i < ds.length; i++) {
            if (prevSrc && ds[i].src === prevSrc) { entry = ds[i]; break; }
        }
        if (!entry) {
            // Fallback: entry ids mirror data-media-index.
            var slide = document.querySelector(
                '.f-product__media[data-media-id="' + CANVAS_MEDIA_ID + '"]');
            var idx = slide ? slide.getAttribute('data-media-index') : null;
            for (i = 0; i < ds.length; i++) {
                if (idx !== null && String(ds[i].id) === String(idx)) { entry = ds[i]; break; }
            }
        }
        if (!entry) return false;

        entry.src = url;
        entry.width = NATURAL;
        entry.height = NATURAL;
        return true;
    }

    function paintCanvas(file, label) {
        var slide = document.querySelector(
            '.f-product__media[data-media-id="' + CANVAS_MEDIA_ID + '"]');
        if (!slide) return false;

        // Already showing this configuration: just make sure it is the visible
        // slide (the shopper may have scrolled the carousel elsewhere).
        if (slide.getAttribute('data-msh-config') === file) {
            showMedia(CANVAS_MEDIA_ID);
            return true;
        }

        var url = CDN + file;
        var alt = 'Composite flat panel shutter pair, ' + label + ' configuration';
        var zoomUrl = url + '&width=' + NATURAL;
        var prevZoom = slide.getAttribute('data-src');   // read BEFORE overwriting

        slide.setAttribute('data-src', zoomUrl);   // input to a future zoom init
        slide.setAttribute('data-pswp-width', NATURAL);
        slide.setAttribute('data-pswp-height', NATURAL);
        slide.setAttribute('data-msh-config', file);

        var img = slide.querySelector('img');
        if (img) {
            img.setAttribute('srcset', srcsetFor(url, MAIN_WIDTHS));
            img.setAttribute('src', url + '&width=' + NATURAL);
            img.setAttribute('alt', alt);
        }

        var thumb = document.querySelector(
            '.f-product__media-thumbnails-item[data-media-id="' + CANVAS_MEDIA_ID + '"]');
        var timg = thumb ? thumb.querySelector('img') : null;
        if (timg) {
            timg.setAttribute('srcset', srcsetFor(url, THUMB_WIDTHS));
            timg.setAttribute('src', url + '&width=246');
            timg.setAttribute('alt', alt);
        }

        repointZoom(prevZoom, zoomUrl);
        showMedia(CANVAS_MEDIA_ID);
        return true;
    }

    /* ----------------------------------------------------------------------
       Springline is only meaningful for an arched shutter.

       Monument shows its springline field only when Arched Top is chosen. The
       app has a Rules engine for exactly this, and the rule was built there
       correctly (Top Profile is one of [Arched Top] -> Show Option: Springline)
       but will not persist: the rule editor's Save silently discards, the same
       way the calculator-level Submit does, and the Rules panel stays empty
       afterwards. Rather than ship a springline field to people buying straight
       shutters, the condition is enforced here.

       The app-side rule remains the proper home for this. If it is ever saved,
       this becomes a harmless second enforcement of the same condition.

       Springline is deliberately left OPTIONAL app-side, so that hiding it here
       can never block add-to-cart on a required-but-invisible field.
       ---------------------------------------------------------------------- */

    function stepByLabel(re) {
        var holders = document.querySelectorAll('.msh-cfg .optionHolder');
        for (var i = 0; i < holders.length; i++) {
            var l = holders[i].querySelector('label.option-label');
            if (l && re.test(txt(l))) return holders[i];
        }
        return null;
    }

    function applySpringlineRule() {
        var spring = stepByLabel(/springline/i);
        if (!spring) return;
        var top = stepByLabel(/top profile/i);
        var arched = false;
        if (top) {
            var checked = top.querySelector('input.swatch-input:checked');
            if (checked) {
                var tile = checked.closest('.swatch-title-holder');
                var t = tile ? tile.querySelector('.swatch-title-text') : null;
                arched = !!t && /arched/i.test(txt(t));
            }
        }
        if (arched) {
            spring.removeAttribute('data-msh-hidden');
            spring.style.removeProperty('display');
        } else {
            spring.setAttribute('data-msh-hidden', 'springline-straight');
            spring.style.display = 'none';
        }
    }

    function syncGallery() {
        // Configuration is the step whose choice drives the product shot.
        var holders = document.querySelectorAll('.msh-cfg .optionHolder');
        for (var i = 0; i < holders.length; i++) {
            var label = holders[i].querySelector('label.option-label');
            if (!label || !/configuration/i.test(txt(label))) continue;
            var checked = holders[i].querySelector('input.swatch-input:checked');
            if (!checked) {
                // Nothing picked yet (page load): still collapse the render
                // thumbnails to one, or a shopper lands on seven near-identical
                // shots before touching the configurator.
                hideOtherConfigThumbs(null);
                return;
            }
            var tile = checked.closest('.swatch-title-holder');
            var t = tile ? tile.querySelector('.swatch-title-text') : null;
            if (!t) return;
            var value = rawText(t) || txt(t);
            /* Product media first (2026-08-24). Gray's rustic panel product
               proved the hardcoded route does not travel: CANVAS_MEDIA_ID is
               the flat panel's slide, so on any other product paintCanvas
               found nothing and the picture never moved, whatever alt text he
               set. The rule the client can actually operate is "upload the
               render to Media, name it after the configuration", so an exact
               alt match wins on every product and the Files map is only the
               fallback for a product that carries no such media (flat panel). */
            /* Paint first, configuration second (2026-09-14).

               A painted shutter looks different from an unfinished one, so once
               a paint finish is chosen the painted render is the truthful photo
               and the bare-wood configuration render is not. Preference order:

                 "50/50 Painted"  - this configuration, painted. Best answer.
                 "Painted"        - one painted shot reused across every
                                    configuration, which is what Gray described
                                    ("the painted images are the same across all
                                    products depending on style").
                 "50/50"          - no painted render uploaded yet, so fall back
                                    to the configuration render rather than
                                    leaving a stale photo on screen.

               Each step is an EXACT alt match, so a product that carries none of
               them lands on the existing partial-match path unchanged. */
            var finish = selectedPaintFinish();
            var mediaId = null;
            /* Colour first when Painted has one (2026-09-27, Gray: "Would the
               alt text be 50/50 Painted Black for it to show that image?").
               "50/50 Painted Black", then "Painted Black", then the colourless
               chain below, so a product with one painted render per
               configuration keeps working unchanged. */
            var color = finish === 'painted' ? selectedPaintColor() : '';
            if (color) {
                mediaId = mediaIdForValue(value + ' ' + finish + ' ' + color, true)
                       || mediaIdForValue(finish + ' ' + color, true);
            }
            if (finish && !mediaId) {
                mediaId = mediaIdForValue(value + ' ' + finish, true)
                       || mediaIdForValue(finish, true);
            }
            if (!mediaId) mediaId = mediaIdForValue(value, true);
            if (mediaId) {
                showMedia(mediaId);
                hideOtherConfigThumbs(mediaId);
                return;
            }
            var file = CONFIG_IMAGES[normalise(value)];
            if (file) paintCanvas(file, value);
            else showMedia(mediaIdForValue(value));   // partial alt match, last resort
            return;
        }
    }

    /* Seven renders as product media would mean seven near-identical thumbnails,
       which is why the canvas route existed. Keep the strip clean instead: a
       thumbnail whose alt is a configuration name is shown only while it is the
       selected configuration. Real photographs (any other alt) are untouched. */
    function hideOtherConfigThumbs(activeId) {
        var thumbs = document.querySelectorAll('.f-product__media-thumbnails-item[data-media-id]');
        var CONFIG_KEYS = renderKeys();
        if (!activeId) {
            // No selection: keep the first render thumbnail, hide the rest.
            for (var j = 0; j < thumbs.length; j++) {
                var im = thumbs[j].querySelector('img');
                if (im && CONFIG_KEYS[normalise(im.getAttribute('alt'))]) {
                    activeId = thumbs[j].getAttribute('data-media-id');
                    break;
                }
            }
            if (!activeId) return;
        }
        for (var i = 0; i < thumbs.length; i++) {
            var img = thumbs[i].querySelector('img');
            var key = img ? normalise(img.getAttribute('alt')) : '';
            if (!key || !CONFIG_KEYS[key]) continue;
            var mine = thumbs[i].getAttribute('data-media-id') === activeId;
            thumbs[i].style.display = mine ? '' : 'none';
        }
    }

    /* Mark answered steps so the rail badge flips to the accent colour. */
    function refreshProgress(shell) {
        shell.querySelectorAll('.optionHolder').forEach(function (h) {
            var answered = false;
            if (h.querySelector('input.swatch-input:checked')) answered = true;
            h.querySelectorAll('.option-number-input, .opc-quantity').forEach(function (i) {
                if (i.value && String(i.value).trim() !== '') answered = true;
            });
            // Dimensions became two plain selects on 2026-08-16 (Monument parity),
            // so the number-input check alone stopped detecting a completed step.
            // "-- Select --" is the app's empty value and must not count.
            h.querySelectorAll('select.option-dropdown-input').forEach(function (s) {
                var v = (s.value || '').trim();
                if (v && !/^--/.test(v)) answered = true;
            });
            if (answered) h.setAttribute(DONE_ATTR, '1');
            else h.removeAttribute(DONE_ATTR);
        });
    }

    function enhance() {
        var stepHolder = document.querySelector('.stepHolder');
        if (!stepHolder) return false;

        var shell = buildShell(stepHolder);

        var holders = shell.querySelectorAll('.optionHolder');
        holders.forEach(function (h, i) {
            enhanceHeading(h, i + 1);
            enhanceUpcharges(h);
            // After enhanceUpcharges: the chip is appended into .swatch-title-text
            // and the key is read from that node's text children only, so ordering
            // is not load-bearing - but keeping it after matches the DOM the
            // MutationObserver re-runs against.
            enhanceTileImages(h);
            enhanceSpringlineHelp(h);
            hideDeadChoices(h);
            hidePaintChoices(h);
            groupDimensions(h);
            fixQuantityLimits(h);
        });

        orderSteps(shell);
        enhancePrice(shell);
        hidePaintSubSteps();
        applySpringlineRule();
        refreshProgress(shell);
        shell.setAttribute(READY_ATTR, '1');
        return true;
    }

    var tries = 0;
    var observer = null;

    function boot() {
        if (enhance()) {
            /* The desktop sticky-media pair in msh-opc-fixes.css used to be keyed
               to .product-composite, which meant it could only ever match the one
               composite product (2026-09-09, Gray's pine product). It is keyed to
               this class now: set here, after the shell actually exists, so it
               follows the configurator onto every material and never lands on a
               product page that has no calculator. */
            document.body.classList.add('msh-cfg-page');
            var shell = document.querySelector('.msh-cfg');
            if (shell && !observer) {
                // The app rewrites the price node on every recalculation, which
                // wipes our caption. Re-apply, but never inside our own writes.
                var pending = false;
                observer = new MutationObserver(function () {
                    if (pending) return;
                    pending = true;
                    requestAnimationFrame(function () {
                        pending = false;
                        observer.disconnect();
                        enhance();
                        observer.observe(shell, { childList: true, subtree: true });
                    });
                });
                observer.observe(shell, { childList: true, subtree: true });
            }
            document.addEventListener('change', function () {
                var s = document.querySelector('.msh-cfg');
                if (!s) return;
                refreshProgress(s);
                /* Re-assert the paint hide on the same tick as the change.
                   enhance() also does this whenever the app re-renders, but the
                   observer fires a frame later at the earliest - and hiding a
                   priced choice is not something to leave a frame of. */
                s.querySelectorAll('.optionHolder').forEach(hidePaintChoices);
                hidePaintSubSteps();
                syncGallery();
                applySpringlineRule();
            }, true);
            syncGallery();
            // Warm the render set while the shopper reads the page, not while
            // they wait for a switch.
            if (window.requestIdleCallback) {
                requestIdleCallback(preloadRenders, { timeout: 4000 });
            } else {
                setTimeout(preloadRenders, 2500);
            }
            return;
        }
        if (++tries < 60) setTimeout(boot, 400);   // app injects async, up to ~24s
    }

    /* The 400ms poll above is what let the app paint priced tiles before we got
       a turn (see the PRE-PAINT GATE note). Watch for the calculator instead of
       waiting for the next tick: a MutationObserver callback runs before the
       browser paints, so enhance() lands in the same frame the app injects.
       The poll stays as the fallback for anything the observer misses. */
    (function watchForShell() {
        if (!window.MutationObserver) return;
        var early = new MutationObserver(function () {
            if (!document.querySelector('.msh-cfg')) return;
            early.disconnect();
            boot();
        });
        early.observe(document.documentElement, { childList: true, subtree: true });
        // stop watching once the app has had its ~24s, so we never observe forever
        setTimeout(function () { early.disconnect(); }, 25000);
    })();

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', boot);
    } else {
        boot();
    }
})();
